import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { Box, Button, LinearProgress, SelectChangeEvent } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import CustomInput from '../../../components/muiComponents/customInput/CustomInput'
import SingleSelect from '../../../components/muiComponents/singleSelect/SingleSelect'
import { groupApiSlice } from '../../../features/group/groupSlice'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import { capitalize } from '../../../utils/functions'
import Breadcrumb from '../../../components/Breadcrumb'
import SectionAnalysisTable from './components/SectionAnalysisTable'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'

const styles = {
    boxContainer: {
        margin: '10px',
        borderRadius: '16px',
    },
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    search: {
        width: '440px',
    },
}

const TeacherSectionAnalysisPage = () => {
    const { formatMessage, locale } = useIntl()
    const { state } = useLocation()
    const { courseSubjectId, sectionId, typeEnum, language } = useParams()
    const [groupId, setGroupId] = useState<number>()
    const [filter, setFilter] = useState<string>('')
    // Поиск применяется кнопкой, а не на каждое нажатие клавиши
    const [search, setSearch] = useState<string>('')
    const { data: groups = [] } = groupApiSlice.useGetGroupsForTeacherQuery({
        courseSubjectId: +courseSubjectId,
        languageEnum: language,
    })
    const [fetchAnalysis] = sectionApiSlice.useLazyGetStudentSectionAnalysisQuery()

    const { rows, isLoading: isFetching, loadedPages, sentinelRef } =
        usePaginatedList(
            (page, size) =>
                fetchAnalysis({
                    id: +sectionId,
                    typeEnum,
                    groupId,
                    filter: search,
                    page,
                    rowsPerPage: size,
                }).unwrap(),
            [sectionId, typeEnum, groupId, search],
        )

    const handleGroupChange = (event: SelectChangeEvent<typeof groupId>) => {
        const {
            target: { value },
        } = event

        setGroupId(+value)
    }

    const getBreadcrumb = () => {
        const courseTitle = state?.course
            ? state?.course?.[`title${capitalize(locale)}`]
            : ''
        const subjectTitle = state?.subject
            ? state?.subject?.[`title${capitalize(locale)}`]
            : ''
        const sectionTitle = state?.section ? state?.section?.title : ''

        return (
            <Breadcrumb
                links={[
                    formatMessage({ id: 'myCourses' }),
                    courseTitle,
                    subjectTitle,
                    sectionTitle,
                ]}
            />
        )
    }

    return (
        <Box sx={styles.boxContainer}>
            {getBreadcrumb()}

            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'videoAndTestAnalysis' })}</H3>
                    </Title>
                </Header>

                <Box sx={styles.filterBox}>
                    <Box sx={styles.searchBox}>
                        <CustomInput
                            sx={styles.search}
                            value={filter}
                            margin="none"
                            type="text"
                            placeholder="Оқушы атын іздеу"
                            size="small"
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') setSearch(filter)
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => setSearch(filter)}
                        >
                            {formatMessage({ id: 'search' })}
                        </Button>
                    </Box>

                    <SingleSelect
                        id="languageEnum"
                        name="languageEnum"
                        value={groupId}
                        handleChange={handleGroupChange}
                        placeholder={formatMessage({
                            id: 'group',
                        })}
                        menuItems={groups}
                        small={true}
                        marginTop={0}
                    ></SingleSelect>
                </Box>

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        <SectionAnalysisTable
                            analysis={rows}
                            isFetching={isFetching}
                            typeEnum={typeEnum}
                        />

                        <Sentinel ref={sentinelRef} isLoading={isFetching} />
                    </>
                )}
            </Content>
        </Box>
    )
}

export default TeacherSectionAnalysisPage

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
